import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ThreatFeed from '../components/ThreatFeed';
import SettingsPage from '../pages/SettingsPage';
import ROIPage from '../pages/ROIPage';
import SourcesPage from '../pages/SourcesPage';
import { ThreatTestsPanel } from '../components/ThreatTestsPanel';
import type { Incident } from '../lib/incident';
import type { Needs } from '../lib/needs';
import {
  DEFAULT_SETTINGS_SECTION,
  ROUTES,
  campaignPath,
  entityPath,
  isFullBleedPath,
  isLegacyDarkPath,
  sourcesIntentFromParams,
  sourcesPath,
  type SourcesIntent,
} from './paths';
import { themeFor, useAppearance, useDocumentTheme } from './appearance';
import EntityRoute from './EntityRoute';
import CampaignRoute from './CampaignRoute';
import IncidentRedirect from './IncidentRedirect';
import RouteMessage from './RouteMessage';
import NeedsYouStrip from './NeedsYouStrip';
import SignOutDialog from './SignOutDialog';
import { ToastRegion, useToast } from './ToastProvider';
import TopBar from './TopBar';
import { blastScoreRouteId } from './resolvers';
import { sectionFromHash, sectionHash, type SectionId } from './sections';
import { useActiveSection } from './useActiveSection';
import { useGoBack } from './useGoBack';
import { takeReturnPath } from './returnPath';
import { useQueryParam, useUpdateQuery } from './useQueryParam';
import { useScrollRestoration } from './useScrollRestoration';
import {
  startAwsThreat,
  startOktaThreat,
  type AwsTestSubhandler,
  type AwsOktaSubhandler,
} from '../api-utils/tests';
import type { BlastRadiusScoreDocument } from '../api-utils/blast';
import { useWorkspace } from '../workspace';
import {
  AgentRankModal,
  AvailableSourcesDialog,
  IncidentSummaryModal,
  IntelligenceBriefSlideOver,
  CampaignsModal,
  EntityDossierModal,
  InvestigationWorkspace,
  MetricsBand,
} from '../components/c3';
import { useBandRoute } from '../hooks/useBandRoute';
import { useDataMode } from '../data';
import {
  DEFAULT_RANKS,
  DEFAULT_FLOW,
  makeC3Incidents,
  C3_ENTITIES,
  type C3MockIncident,
  type C3Rank,
  type C3FlowCounts,
  type C3Entity,
} from '../mock/c3';

/** Okta threat options (real backend subhandlers). */
const OKTA_TEST_THREAT_OPTIONS: { id: AwsOktaSubhandler; label: string }[] = [
  { id: 'brute_force_password_spray', label: 'Brute force login' },
  { id: 'admin_role_escalation', label: 'Privilege escalation' },
  { id: 'impossible_travel', label: 'Session hijacking' },
  { id: 'mfa_reset_suspicious_login', label: 'MFA bypass attempt' },
  { id: 'dormant_account_reactivation', label: 'Dormant account reactivation' },
];

interface User {
  username: string;
  email?: string;
}

interface AppShellProps {
  user: User;
  onSignOut: () => void;
}

/** The demo incident as the remediation view reads it (`raw.c3Mock` keeps the original). */
const mockToIncidentShell = (mock: C3MockIncident): Incident => ({
  id: mock.id,
  source: mock.source.toLowerCase().includes('okta')
    ? 'okta'
    : mock.source.toLowerCase().includes('crowd')
      ? 'crowdstrike'
      : 'aws',
  type: mock.title,
  severity: mock.sev === 'info' ? 'low' : (mock.sev as Incident['severity']),
  title: mock.title,
  description: mock.detail.reasoning,
  timestamp: new Date(),
  status:
    mock.state === 'auto'
      ? 'remediated'
      : mock.state === 'progress'
        ? 'analyzing'
        : 'detected',
  raw: { c3Mock: mock },
});

const AppShell: React.FC<AppShellProps> = ({ user, onSignOut }) => {
  const { org } = useWorkspace();
  const navigate = useNavigate();
  const { pathname, hash, search: locationSearch } = useLocation();
  const [searchParams] = useSearchParams();
  const updateQuery = useUpdateQuery();
  const goBack = useGoBack();
  const { tab, showTab, setTab } = useBandRoute();
  const isConsole = pathname === ROUTES.console;
  const { active: activeSection, goTo: goToSection } = useActiveSection(isConsole);
  useScrollRestoration();

  // Google sign-in lands on `/`: go on to the link the person came with, once.
  const returnChecked = useRef(false);
  useEffect(() => {
    if (returnChecked.current) return;
    returnChecked.current = true;
    const saved = takeReturnPath();
    if (saved && pathname === ROUTES.console && !locationSearch && !hash) navigate(saved, { replace: true });
  }, [pathname, locationSearch, hash, navigate]);
  /** Views still written for a dark surface always render in Normal until they are rebuilt (week 3). */
  const legacyDark = isLegacyDarkPath(pathname);
  const { appearance } = useAppearance();
  const theme = themeFor(appearance, legacyDark);
  useDocumentTheme(theme);
  const settingsSection = useMemo(() => {
    const [, base, section] = pathname.split('/');
    return base === 'settings' && section ? decodeURIComponent(section) : DEFAULT_SETTINGS_SECTION;
  }, [pathname]);
  const sourcesIntent = sourcesIntentFromParams(searchParams);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [, setFeedView] = useQueryParam<'active' | 'suppressed'>('view', 'active');
  const [ebrGraphFocus, setEbrGraphFocus] = useState<{ id: string; label: string } | null>(null);
  // A scope the Threat Feed hands to the Workspace, and the view that shows it: the graph, or the Threat Index.
  const [feedGraphScope, setFeedGraphScope] = useState<{
    type: 'campaign' | 'threat' | 'entity' | 'everything';
    id: string;
    label: string;
    pane?: 'graph' | 'risk';
  } | null>(null);
  // Demo reads the invented tenant and Live the API; the data provider holds the switch (decisions/005).
  const { demo: demoMode, setMode: setDataMode } = useDataMode();
  const setDemoMode = useCallback((demo: boolean) => setDataMode(demo ? 'demo' : 'live'), [setDataMode]);
  const [autoRemediate, setAutoRemediate] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  // The menu of applications that can still be connected: from Connected Sources (tools) or from Threat Intel (feeds).
  const [connectMenu, setConnectMenu] = useState<'tool' | 'intel' | null>(null);
  const [agentRankOpen, setAgentRankOpen] = useState(false);
  const [ranks, setRanks] = useState<C3Rank[]>(() => {
    try {
      const saved = localStorage.getItem('arbitium-c3-ranks');
      return saved ? JSON.parse(saved) : DEFAULT_RANKS.map((r) => ({ ...r }));
    } catch {
      return DEFAULT_RANKS.map((r) => ({ ...r }));
    }
  });
  const [c3Incidents, setC3Incidents] = useState<C3MockIncident[]>(() => makeC3Incidents());
  const [flowCounts, setFlowCounts] = useState<C3FlowCounts>({ ...DEFAULT_FLOW });
  const [summaryIncident, setSummaryIncident] = useState<C3MockIncident | null>(null);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [campaignFocus, setCampaignFocus] = useState<string | undefined>();
  const [dossierEntity, setDossierEntity] = useState<C3Entity | null>(null);
  const { notify } = useToast();
  // What the Threat Feed reports as waiting on a person; the strip and the Threats count read it (item 45).
  const [needs, setNeeds] = useState<Needs | null>(null);
  const notifyError = useCallback((message: string) => notify({ tone: 'error', message }), [notify]);
  const [storming, setStorming] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedAwsTests, setSelectedAwsTests] = useState<Set<AwsTestSubhandler>>(new Set());
  const [selectedCrowdstrikeTests, setSelectedCrowdstrikeTests] = useState<Set<number>>(new Set());
  const [selectedOktaTests, setSelectedOktaTests] = useState<Set<number>>(new Set());
  const [selectedCrossDomainTests, setSelectedCrossDomainTests] = useState<Set<number>>(new Set());

  useEffect(() => {
    localStorage.setItem('arbitium-c3-ranks', JSON.stringify(ranks));
  }, [ranks]);

  // The top bar sends another view here as `/#respond`: scroll to the section, then drop the hash.
  useEffect(() => {
    if (!isConsole || !hash) return;
    const section = sectionFromHash(hash);
    if (!section) return;
    goToSection(section.id, 'auto');
    navigate({ pathname: ROUTES.console, search: locationSearch }, { replace: true });
  }, [isConsole, hash, locationSearch, goToSection, navigate]);

  const handleSection = useCallback(
    (id: SectionId) => {
      if (isConsole) goToSection(id);
      else navigate({ pathname: ROUTES.console, hash: sectionHash(id) });
    },
    [isConsole, goToSection, navigate]
  );

  useEffect(() => {
    setEbrGraphFocus(null);
    setFeedGraphScope(null);
    setSummaryIncident(null);
  }, [org]);

  const launchSelectedAwsTests = useCallback(async () => {
    const list = Array.from(selectedAwsTests);
    if (list.length === 0) return;
    notify(`Starting ${list.length} AWS test(s)…`);
    let started = 0;
    for (let i = 0; i < list.length; i++) {
      const subhandler = list[i];
      try {
        const res = await startAwsThreat(subhandler);
        const ok = res?.success === true;
        if (ok) {
          started += 1;
          notify(`Starting AWS test "${subhandler}"…`);
        } else {
          const msg = (res?.output as any)?.[0]?.error || (res as any)?.error || 'Failed to start test';
          notifyError(msg);
        }
      } catch (e) {
        notifyError(e instanceof Error ? e.message : String(e));
      }
      if (i < list.length - 1) {
        await new Promise((r) => setTimeout(r, 250));
      }
    }
    notify({
      tone: started > 0 ? 'success' : 'info',
      message:
        started > 0
          ? `${started}/${list.length} AWS test(s) started. Check Monitor tests for status.`
          : 'No AWS tests were started.',
    });
    setSelectedAwsTests(new Set());
  }, [selectedAwsTests, notify, notifyError]);

  const launchSelectedOktaTests = useCallback(async () => {
    const list = Array.from(selectedOktaTests);
    if (list.length === 0) return;
    notify(`Starting ${list.length} Okta test(s)…`);
    let started = 0;

    for (let i = 0; i < list.length; i++) {
      const idx = list[i];
      const option = OKTA_TEST_THREAT_OPTIONS[idx];
      if (!option) continue;
      const subhandler = option.id;
      try {
        const res = await startOktaThreat(subhandler);
        const ok = res?.success === true;
        if (ok) {
          started += 1;
          notify(`Starting Okta test "${subhandler}"…`);
        } else {
          const msg =
            (res?.output as any)?.[0]?.error || (res as any)?.error || 'Failed to start test';
          notifyError(msg);
        }
      } catch (e) {
        notifyError(e instanceof Error ? e.message : String(e));
      }
      if (i < list.length - 1) {
        await new Promise((r) => setTimeout(r, 250));
      }
    }

    notify({
      tone: started > 0 ? 'success' : 'info',
      message:
        started > 0
          ? `${started}/${list.length} Okta test(s) started. Check Monitor tests for status.`
          : 'No Okta tests were started.',
    });
    setSelectedOktaTests(new Set());
  }, [selectedOktaTests, notify, notifyError]);

  // Incidents the console used to invent in Live mode, and detections it pulled from CrowdStrike, were
  // kept in the browser. Neither is made any more: drop what an earlier session left.
  useEffect(() => {
    localStorage.removeItem('arbitium-incidents');
  }, []);

  useEffect(() => {
    if (!isConsole) {
      setSelectedIds(new Set());
    }
  }, [isConsole]);

  // Nothing selects incidents in the console any more; the two Settings buttons that cleared a selection and
  // the local store stay until Settings is rebuilt (week 3).
  const closeSelected = () => setSelectedIds(new Set());
  const resetAll = () => {
    localStorage.removeItem('arbitium-incidents');
    setSelectedIds(new Set());
  };

  const openSummaryById = (id: string) => {
    const mock = c3Incidents.find((m) => m.id === id);
    if (mock) setSummaryIncident(mock);
  };

  /** An incident opens the campaign it belongs to (a lone incident is a campaign of one); the route resolves which. */
  const openCampaign = (id: string) => {
    setSummaryIncident(null);
    navigate(campaignPath(id));
  };

  const handleIncidentSelect = (incident: Incident) => openCampaign(incident.id);

  const openRemediationFromSummary = () => {
    if (summaryIncident) {
      handleIncidentSelect(mockToIncidentShell(summaryIncident));
    }
  };

  const runStorm = () => {
    if (storming) return;
    setStorming(true);
    const from = flowCounts.raw;
    const burst = 3912;
    const dur = 6000;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      const ease = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      const cur = Math.round(from + burst * ease);
      setFlowCounts((prev) => ({
        ...prev,
        raw: cur,
        dedup: Math.round((cur * (18 + 13 * ease)) / 100),
        anomalies: Math.round(1204 + 37 * ease),
        fp: Math.round(693 + 35 * ease),
      }));
      if (p < 1) requestAnimationFrame(tick);
      else {
        setStorming(false);
        notify({
          tone: 'success',
          title: 'Storm absorbed.',
          message: '3,912 alerts in 6 seconds → clustered → suppressed FPs. Active incidents unchanged. Analysts paged: 0.',
        });
      }
    };
    requestAnimationFrame(tick);
  };

  const resetDemo = () => {
    setC3Incidents(makeC3Incidents());
    setFlowCounts({ ...DEFAULT_FLOW });
    setRanks(DEFAULT_RANKS.map((r) => ({ ...r })));
    setStorming(false);
    notify({ title: 'Demo reset.', message: 'Mock counters, incidents, and Agent Rank settings restored.' });
  };

  const openEbrDetail = useCallback(
    (score: BlastRadiusScoreDocument) => {
      navigate(entityPath(blastScoreRouteId(score)), { state: { score } });
    },
    [navigate]
  );

  const openEbrInGraph = useCallback(
    (id: string, name: string) => {
      setEbrGraphFocus({ id, label: name });
      navigate(ROUTES.console);
    },
    [navigate]
  );

  const goToSources = useCallback(
    (intent: SourcesIntent | null = null) => navigate(sourcesPath(intent)),
    [navigate]
  );

  const settingsView = (
    <SettingsPage
      onBack={goBack}
      section={settingsSection}
      demoMode={demoMode}
      setDemoMode={setDemoMode}
      autoRemediate={autoRemediate}
      setAutoRemediate={setAutoRemediate}
      selectedAwsTests={selectedAwsTests}
      setSelectedAwsTests={setSelectedAwsTests}
      selectedCrowdstrikeTests={selectedCrowdstrikeTests}
      setSelectedCrowdstrikeTests={setSelectedCrowdstrikeTests}
      selectedOktaTests={selectedOktaTests}
      setSelectedOktaTests={setSelectedOktaTests}
      selectedCrossDomainTests={selectedCrossDomainTests}
      setSelectedCrossDomainTests={setSelectedCrossDomainTests}
      selectedIdsCount={selectedIds.size}
      onOpenAgentRank={() => setAgentRankOpen(true)}
      onMonitorTests={() => navigate(ROUTES.tests)}
      onLaunchAws={launchSelectedAwsTests}
      onLaunchOkta={launchSelectedOktaTests}
      onComingSoon={(msg) => notify(msg)}
      onCloseSelected={closeSelected}
      onResetAll={resetAll}
    />
  );

  return (
    <div className="c3-app" data-theme={theme}>
      <TopBar
        userLabel={user.email || user.username}
        demoMode={demoMode}
        activeSection={isConsole ? activeSection : null}
        onDark={theme === 'normal'}
        onSection={handleSection}
        onOpenIntel={() => setBriefOpen(true)}
        onRequestSignOut={() => setSignOutOpen(true)}
        pending={needs?.count}
        onStorm={runStorm}
        onNeedle={() => openSummaryById('INC-4021')}
        onReset={resetDemo}
        storming={storming}
      />
      <NeedsYouStrip needs={needs} />

      <main className={`relative z-10 ${isFullBleedPath(pathname) ? '' : 'p-6'}`}>
        <Routes>
          <Route
            path={ROUTES.console}
            element={
              <>
                <MetricsBand
                  tab={tab}
                  onTab={setTab}
                  showTab={showTab}
                  onOpenCampaigns={() => {
                    setCampaignFocus(undefined);
                    setCampaignsOpen(true);
                  }}
                  onOpenConnectors={() => goToSources()}
                  onAddSource={() => setConnectMenu('tool')}
                  onOpenFeed={(view) => {
                    setFeedView(view || 'active');
                    goToSection('respond');
                  }}
                  onShowInFeed={(target) => {
                    // A stage's destination is the feed as that stage sees it: its list, state and type, and no other filter.
                    updateQuery((params) => {
                      const put = (key: string, value: string | undefined, fallback: string) => {
                        if (value && value !== fallback) params.set(key, value);
                        else params.delete(key);
                      };
                      put('view', target.view, 'active');
                      put('state', target.state, 'all');
                      put('type', target.type, 'all');
                      params.delete('source');
                      params.delete('severity');
                      params.delete('time');
                      params.delete('action');
                      params.delete('q');
                    });
                    goToSection('respond');
                  }}
                />
                <InvestigationWorkspace
                  onOpenIncident={openSummaryById}
                  onOpenEntity={(name) => {
                    const ent = C3_ENTITIES.find((e) => e.n === name);
                    if (ent) setDossierEntity(ent);
                  }}
                  canOpenEntity={(name) => C3_ENTITIES.some((e) => e.n === name)}
                  onOpenFeed={() => {
                    document.getElementById('c3-threat-feed')?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }}
                  onOpenEbrDetail={openEbrDetail}
                  externalFocus={ebrGraphFocus}
                  onExternalFocusConsumed={() => setEbrGraphFocus(null)}
                  externalScope={feedGraphScope}
                  onExternalScopeConsumed={() => setFeedGraphScope(null)}
                />
                <ThreatFeed
                  onOpenCampaign={openCampaign}
                  onNeeds={setNeeds}
                  onViewInGraph={(scope) => {
                    setFeedGraphScope(scope);
                    document.getElementById('c3-investigation-workspace')?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }}
                  onViewInIndex={(scope) => {
                    setFeedGraphScope({ ...scope, pane: 'risk' });
                    document.getElementById('c3-investigation-workspace')?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }}
                />
              </>
            }
          />
          <Route
            path={ROUTES.sources}
            element={
              <SourcesPage
                onBack={goBack}
                intent={sourcesIntent}
                onIntentConsumed={() =>
                  updateQuery((params) => {
                    params.delete('connect');
                    params.delete('edit');
                  })
                }
              />
            }
          />
          <Route path={ROUTES.settingsSection} element={settingsView} />
          <Route path={ROUTES.roi} element={<ROIPage onBack={goBack} />} />
          <Route
            path={ROUTES.tests}
            element={<ThreatTestsPanel onBack={goBack} />}
          />
          <Route path={ROUTES.entity} element={<EntityRoute onOpenInGraph={openEbrInGraph} />} />
          <Route path={ROUTES.campaign} element={<CampaignRoute />} />
          <Route path={ROUTES.incident} element={<IncidentRedirect />} />
          <Route
            path="*"
            element={
              <RouteMessage
                title="Page not found"
                detail="This address does not match any page of the console."
                onBack={goBack}
              />
            }
          />
        </Routes>
      </main>

      {signOutOpen && (
        <SignOutDialog
          onCancel={() => setSignOutOpen(false)}
          onConfirm={() => {
            setSignOutOpen(false);
            onSignOut();
          }}
        />
      )}

      <IntelligenceBriefSlideOver
        open={briefOpen}
        onClose={() => setBriefOpen(false)}
        onCiteIncident={(id) => {
          setBriefOpen(false);
          openSummaryById(id);
        }}
        onConnectSource={() => {
          setBriefOpen(false);
          setConnectMenu('intel');
        }}
      />

      {connectMenu && (
        <AvailableSourcesDialog
          kind={connectMenu}
          onClose={() => setConnectMenu(null)}
          onPick={(app) => {
            setConnectMenu(null);
            goToSources({ connect: true, ...(app ? { provider: app.provider, name: app.name } : {}) });
          }}
        />
      )}

      {agentRankOpen && (
        <AgentRankModal
          ranks={ranks}
          onToggle={(i) => {
            // The notice is raised here and not inside the updater, which React may run twice.
            const nextMode: C3Rank['mode'] = ranks[i].mode === 'auto' ? 'gated' : 'auto';
            setRanks((prev) => prev.map((r, idx) => (idx === i ? { ...r, mode: nextMode } : r)));
            notify({
              title: 'Agent Rank updated.',
              message: `“${ranks[i].name}” is now ${nextMode.toUpperCase()}.`,
            });
          }}
          onClose={() => setAgentRankOpen(false)}
        />
      )}

      {summaryIncident && (
        <IncidentSummaryModal
          incident={summaryIncident}
          onClose={() => {
            setSummaryIncident(null);
          }}
          onOpenRemediation={openRemediationFromSummary}
          onOpenCampaign={(id) => {
            setCampaignFocus(id);
            setCampaignsOpen(true);
          }}
          onApprove={(inc) => {
            setC3Incidents((prev) =>
              prev.map((x) => (x.id === inc.id ? { ...inc, state: 'auto' as const } : x))
            );
            notify({
              tone: 'success',
              title: 'Executed.',
              message: 'Approved remediation completed and written to the audit trail.',
            });
          }}
        />
      )}

      {campaignsOpen && (
        <CampaignsModal
          focusId={campaignFocus}
          onClose={() => setCampaignsOpen(false)}
          onSelectMember={(id) => {
            setCampaignsOpen(false);
            openSummaryById(id);
          }}
          onOpenCampaign={(id) => {
            setCampaignsOpen(false);
            openCampaign(id);
          }}
        />
      )}

      {dossierEntity && (
        <EntityDossierModal
          entity={dossierEntity}
          onClose={() => setDossierEntity(null)}
          onSelectIncident={(id) => {
            setDossierEntity(null);
            openSummaryById(id);
          }}
        />
      )}

      <ToastRegion />
    </div>
  );
};

export default AppShell;
